import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import Portal from '../../shared/Portal';
import {
  getProjects, createProject, updateProject, deleteProject,
  getSprints, createSprint, updateSprint, deleteSprint,
  getStories, createStory, updateStory, deleteStory,
  getMembers,
} from './businessApi';

// ─── Language helpers ────────────────────────────────────────────────────────
let _isNl = true;
const t   = (nl, en) => _isNl ? nl : en;
const fmt = (d) => d ? new Date(d).toLocaleDateString(_isNl ? 'nl-NL' : 'en-GB') : '—';

// ─── Constants ───────────────────────────────────────────────────────────────
const PRIORITY_COLORS = {
  critical: '#9B30FF',
  high:     '#C05050',
  medium:   '#C8A84B',
  low:      '#4AC87A',
};

const PRIORITY_LABELS = {
  critical: { nl: 'Kritiek',  en: 'Critical' },
  high:     { nl: 'Hoog',     en: 'High' },
  medium:   { nl: 'Gemiddeld',en: 'Medium' },
  low:      { nl: 'Laag',     en: 'Low' },
};

const STATUS_COLUMN_COLORS = {
  todo:        '#667799',
  in_progress: '#5B9CF6',
  review:      '#C8A84B',
  done:        '#4AC87A',
};

const STATUS_COLUMN_LABELS = {
  todo:        { nl: 'Te Doen',    en: 'To Do' },
  in_progress: { nl: 'Bezig',      en: 'In Progress' },
  review:      { nl: 'Review',     en: 'Review' },
  done:        { nl: 'Klaar',      en: 'Done' },
};

const SPRINT_STATUS_COLORS = {
  planning:  '#C8A84B',
  active:    '#4AC87A',
  completed: '#667799',
};

const SPRINT_STATUS_LABELS = {
  planning:  { nl: 'Planning',   en: 'Planning' },
  active:    { nl: 'Actief',     en: 'Active' },
  completed: { nl: 'Afgerond',   en: 'Completed' },
};

const PROJECT_STATUS_COLORS = {
  active:   '#4AC87A',
  archived: '#667799',
};

const BOARD_COLUMNS = ['todo', 'in_progress', 'review', 'done'];
const PRIORITIES    = ['critical', 'high', 'medium', 'low'];
const STORY_STATUSES= ['backlog', 'todo', 'in_progress', 'review', 'done'];

// ─── Style helpers ────────────────────────────────────────────────────────────
const sheet = {
  background: '#14141a',
  borderRadius: '20px 20px 0 0',
  padding: '24px 20px 40px',
  position: 'fixed',
  bottom: 0, left: 0, right: 0,
  zIndex: 200,
  maxHeight: '90vh',
  overflowY: 'auto',
};

const panel = (x = {}) => ({
  background: '#1e1e28',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 14,
  padding: 16,
  ...x,
});

const inputStyle = (x = {}) => ({
  background: '#12121a',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  padding: '10px 14px',
  color: C.white,
  fontSize: 14,
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: 'Inter, system-ui, sans-serif',
  ...x,
});

const labelStyle = { color: C.silver, fontSize: 12, marginBottom: 4, display: 'block' };

// ─── Energy Coach (shared logic) ──────────────────────────────────────────────
const _MENTAL_BASE = { critical: 40, high: 28, medium: 16, low: 8, none: 5 };
const _PHYS_BASE   = { critical: 22, high: 16, medium: 10, low: 5, none: 3 };
const _MENTAL_KW   = ['analys','design','ontwerp','plan','rapport','report','review','research','onderzoek','presentat','meeting','vergadering','deadline','urgent','strateg','complex','besliss','beoordel','voorstel','proposal','evaluate'];
const _PHYS_KW     = ['install','bouwen','build','carry','draag','move','verplaats','repair','repareer','fix','transport','ophalen','leveren','deliver','opbouw','afbraak','klus','montage'];

function calcStoryEnergy(story, allStories) {
  if (!story.assigned_to) return null;
  const desc = ((story.title || '') + ' ' + (story.description || '')).toLowerCase();
  const mentalHits = _MENTAL_KW.filter(k => desc.includes(k)).length;
  const physHits   = _PHYS_KW.filter(k => desc.includes(k)).length;
  const spPressure = story.story_points ? Math.min(20, story.story_points * 2) : 0;
  const mentalCost = Math.min(95, (_MENTAL_BASE[story.priority] || 5) + mentalHits * 5 + spPressure);
  const physCost   = Math.min(95, (_PHYS_BASE[story.priority]   || 3) + physHits  * 8);
  const peers = (allStories || []).filter(s =>
    s.id !== story.id && s.assigned_to === story.assigned_to && s.status !== 'done'
  );
  const wMental = Math.min(70, peers.reduce((s, x) => s + (_MENTAL_BASE[x.priority] || 5), 0));
  const wPhys   = Math.min(70, peers.reduce((s, x) => s + (_PHYS_BASE[x.priority]   || 3), 0));
  const mentalLeft = Math.max(0, 100 - wMental - mentalCost);
  const physLeft   = Math.max(0, 100 - wPhys   - physCost);
  return { mentalCost, physCost, mentalLeft, physLeft,
    overloaded: mentalLeft < 15 || physLeft < 15,
    atRisk:     mentalLeft < 30 || physLeft < 30,
    peerCount:  peers.length };
}

function StoryRing({ pct, color, label, size = 66 }) {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={8} />
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={8}
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color }}>{Math.round(pct)}%</span>
        </div>
      </div>
      <span style={{ fontSize: 9, color: C.silver, textAlign: 'center', maxWidth: size, lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

function StoryEnergyPanel({ story, allStories, lang }) {
  const isNl = lang === 'nl';
  const e = calcStoryEnergy(story, allStories);
  if (!e) return (
    <div style={{ padding: '8px 0', color: C.silver, fontSize: 11 }}>
      {isNl ? 'Wijs een teamlid toe voor energieanalyse.' : 'Assign a member to see energy analysis.'}
    </div>
  );
  const mc = e.mentalCost > 60 ? '#C05050' : e.mentalCost > 35 ? '#C8A84B' : '#4AC87A';
  const pc = e.physCost > 60   ? '#C05050' : e.physCost > 35   ? '#C8A84B' : '#4AC87A';
  const lm = e.mentalLeft < 15 ? '#C05050' : e.mentalLeft < 30 ? '#C8A84B' : '#4AC87A';
  const lp = e.physLeft < 15   ? '#C05050' : e.physLeft < 30   ? '#C8A84B' : '#4AC87A';
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: '#0e0e16', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 12 }}>⚡</span>
        <span style={{ color: C.white, fontSize: 11, fontWeight: 700 }}>
          {isNl ? 'Energiecoach' : 'Energy Coach'}
        </span>
        {e.peerCount > 0 && (
          <span style={{ color: C.silver2, fontSize: 10, marginLeft: 'auto' }}>
            +{e.peerCount} {isNl ? 'actief' : 'active'}
          </span>
        )}
      </div>
      {e.overloaded ? (
        <div style={{ background: '#C0505018', border: '1px solid #C0505044', borderRadius: 7, padding: '7px 10px', marginBottom: 8, fontSize: 11, color: '#C05050', display: 'flex', gap: 6 }}>
          <span>🚨</span>
          <span>{isNl ? 'Overbelastingsrisico! Overweeg herverdeling.' : 'Overload risk! Consider redistribution.'}</span>
        </div>
      ) : e.atRisk ? (
        <div style={{ background: '#C8A84B18', border: '1px solid #C8A84B44', borderRadius: 7, padding: '7px 10px', marginBottom: 8, fontSize: 11, color: '#C8A84B', display: 'flex', gap: 6 }}>
          <span>⚠️</span>
          <span>{isNl ? 'Hoge belasting — plan hersteltijd.' : 'High load — schedule recovery time.'}</span>
        </div>
      ) : (
        <div style={{ background: '#4AC87A18', border: '1px solid #4AC87A44', borderRadius: 7, padding: '6px 10px', marginBottom: 8, fontSize: 11, color: '#4AC87A', display: 'flex', gap: 6 }}>
          <span>✅</span>
          <span>{isNl ? 'Gezonde belasting.' : 'Healthy load.'}</span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-around' }}>
        <StoryRing pct={e.mentalCost}  color={mc} label={isNl ? 'Mentale\nbelasting' : 'Mental\ncost'} />
        <StoryRing pct={e.physCost}    color={pc} label={isNl ? 'Fysieke\nbelasting' : 'Physical\ncost'} />
        <StoryRing pct={e.mentalLeft}  color={lm} label={isNl ? 'Mentale\nreserve' : 'Mental\nreserve'} />
        <StoryRing pct={e.physLeft}    color={lp} label={isNl ? 'Fysieke\nreserve' : 'Physical\nreserve'} />
      </div>
    </div>
  );
}

const btnPrimary = (x = {}) => ({
  background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`,
  color: '#0a1220',
  border: 'none',
  borderRadius: 10,
  padding: '9px 18px',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  ...x,
});

const btnGhost = (x = {}) => ({
  background: 'transparent',
  border: `1px solid ${_T.accent}55`,
  color: _T.accent,
  borderRadius: 10,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: 13,
  ...x,
});

const btnDanger = (x = {}) => ({
  background: '#C05050',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: 13,
  ...x,
});

const overlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  zIndex: 199,
};

// ─── Overlay + Sheet wrapper ──────────────────────────────────────────────────
function Sheet({ onClose, children }) {
  return (
    <Portal>
      <div style={overlay} onClick={onClose} />
      <div style={sheet}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)', margin: '0 auto 20px' }} />
        {children}
      </div>
    </Portal>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
function Badge({ color, children, small }) {
  return (
    <span style={{
      background: `${color}22`,
      color,
      border: `1px solid ${color}44`,
      borderRadius: 20,
      padding: small ? '2px 8px' : '3px 10px',
      fontSize: small ? 11 : 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

// ─── Story Card ───────────────────────────────────────────────────────────────
function StoryCard({ story, onPress, compact, allStories, lang }) {
  const [showEnergy, setShowEnergy] = useState(false);
  const pc = PRIORITY_COLORS[story.priority] || C.silver;
  const isNl = lang === 'nl';
  return (
    <div
      style={{
        background: '#1e1e28',
        border: '1px solid rgba(255,255,255,0.08)',
        borderLeft: `3px solid ${pc}`,
        borderRadius: 10,
        padding: compact ? '10px 12px' : '12px 14px',
        marginBottom: 8,
        cursor: onPress ? 'pointer' : 'default',
      }}
    >
      <div
        onClick={() => onPress && onPress(story)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ color: C.white, fontSize: 13, fontWeight: 500, flex: 1, lineHeight: 1.4 }}>
          {story.title}
        </span>
        {story.story_points != null && (
          <span style={{
            background: 'rgba(255,255,255,0.08)',
            color: C.silver,
            borderRadius: 6,
            padding: '2px 7px',
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}>
            {story.story_points} SP
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 5 }}>
        {story.assigned_to_name ? (
          <div style={{ color: C.silver, fontSize: 11 }}>
            👤 {story.assigned_to_name}
          </div>
        ) : <div />}
        {/* Energy toggle */}
        <button
          onClick={e => { e.stopPropagation(); setShowEnergy(v => !v); }}
          style={{
            background: showEnergy ? '#5B9CF622' : 'transparent',
            border: `1px solid ${showEnergy ? '#5B9CF644' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 5,
            color: showEnergy ? '#5B9CF6' : C.silver2,
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 6px',
            cursor: 'pointer',
          }}
        >
          ⚡ {isNl ? 'Energie' : 'Energy'}
        </button>
      </div>
      {showEnergy && allStories && (
        <StoryEnergyPanel story={story} allStories={allStories} lang={lang || 'nl'} />
      )}
    </div>
  );
}

// ─── Project Form Sheet ───────────────────────────────────────────────────────
function ProjectFormSheet({ initial, onSave, onClose, lang }) {
  _isNl = lang === 'nl';
  const [form, setForm] = useState({
    name: initial?.name || '',
    description: initial?.description || '',
    status: initial?.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try { await onSave(form); } catch (e) { setError(e.message || t('Opslaan mislukt.', 'Save failed.')); } finally { setSaving(false); }
  };

  return (
    <Sheet onClose={onClose}>
      <div style={{ color: C.white, fontWeight: 700, fontSize: 17, marginBottom: 20 }}>
        {initial ? t('Project bewerken', 'Edit Project') : t('Nieuw project', 'New Project')}
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('Naam', 'Name')} *</label>
        <input
          style={inputStyle()}
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder={t('Projectnaam', 'Project name')}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('Omschrijving', 'Description')}</label>
        <textarea
          style={inputStyle({ minHeight: 80, resize: 'vertical' })}
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder={t('Optionele omschrijving', 'Optional description')}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>{t('Status', 'Status')}</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {['active', 'archived'].map(s => (
            <button
              key={s}
              onClick={() => setForm(f => ({ ...f, status: s }))}
              style={{
                ...btnGhost(),
                background: form.status === s ? `${PROJECT_STATUS_COLORS[s]}22` : 'transparent',
                borderColor: form.status === s ? PROJECT_STATUS_COLORS[s] : 'rgba(255,255,255,0.15)',
                color: form.status === s ? PROJECT_STATUS_COLORS[s] : C.silver,
              }}
            >
              {s === 'active' ? t('Actief', 'Active') : t('Gearchiveerd', 'Archived')}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ background: '#C0505022', border: '1px solid #C0505044', borderRadius: 8, padding: '8px 12px', color: '#C05050', fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button style={btnGhost({ flex: 1 })} onClick={onClose}>
          {t('Annuleren', 'Cancel')}
        </button>
        <button style={btnPrimary({ flex: 1 })} onClick={handleSave} disabled={saving}>
          {saving ? t('Opslaan…', 'Saving…') : t('Opslaan', 'Save')}
        </button>
      </div>
    </Sheet>
  );
}

// ─── Sprint Form Sheet ────────────────────────────────────────────────────────
function SprintFormSheet({ initial, onSave, onClose, lang }) {
  _isNl = lang === 'nl';
  const [form, setForm] = useState({
    name:       initial?.name       || '',
    goal:       initial?.goal       || '',
    start_date: initial?.start_date || '',
    end_date:   initial?.end_date   || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <Sheet onClose={onClose}>
      <div style={{ color: C.white, fontWeight: 700, fontSize: 17, marginBottom: 20 }}>
        {initial ? t('Sprint bewerken', 'Edit Sprint') : t('Nieuwe sprint', 'New Sprint')}
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('Naam', 'Name')} *</label>
        <input
          style={inputStyle()}
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="Sprint 1"
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('Doel', 'Goal')}</label>
        <textarea
          style={inputStyle({ minHeight: 70, resize: 'vertical' })}
          value={form.goal}
          onChange={e => setForm(f => ({ ...f, goal: e.target.value }))}
          placeholder={t('Sprint doel…', 'Sprint goal…')}
        />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>{t('Startdatum', 'Start date')}</label>
          <input
            type="date"
            style={inputStyle()}
            value={form.start_date}
            onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>{t('Einddatum', 'End date')}</label>
          <input
            type="date"
            style={inputStyle()}
            value={form.end_date}
            onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button style={btnGhost({ flex: 1 })} onClick={onClose}>
          {t('Annuleren', 'Cancel')}
        </button>
        <button style={btnPrimary({ flex: 1 })} onClick={handleSave} disabled={saving}>
          {saving ? t('Opslaan…', 'Saving…') : t('Opslaan', 'Save')}
        </button>
      </div>
    </Sheet>
  );
}

// ─── Story Form Sheet ─────────────────────────────────────────────────────────
function StoryFormSheet({ initial, sprints, members, onSave, onClose, lang, defaultStatus }) {
  _isNl = lang === 'nl';
  const [form, setForm] = useState({
    title:       initial?.title       || '',
    description: initial?.description || '',
    story_points: initial?.story_points != null ? String(initial.story_points) : '',
    priority:    initial?.priority    || 'medium',
    assigned_to: initial?.assigned_to || '',
    status:      initial?.status      || defaultStatus || 'backlog',
    sprint_id:   initial?.sprint_id   || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = {
      ...form,
      story_points: form.story_points !== '' ? Number(form.story_points) : null,
      sprint_id:    form.sprint_id || null,
      assigned_to:  form.assigned_to || null,
    };
    try { await onSave(payload); } finally { setSaving(false); }
  };

  return (
    <Sheet onClose={onClose}>
      <div style={{ color: C.white, fontWeight: 700, fontSize: 17, marginBottom: 20 }}>
        {initial ? t('Story bewerken', 'Edit Story') : t('Nieuwe story', 'New Story')}
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('Titel', 'Title')} *</label>
        <input
          style={inputStyle()}
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder={t('Als gebruiker wil ik…', 'As a user I want…')}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('Omschrijving', 'Description')}</label>
        <textarea
          style={inputStyle({ minHeight: 70, resize: 'vertical' })}
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder={t('Acceptatiecriteria, details…', 'Acceptance criteria, details…')}
        />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>{t('Story punten', 'Story points')}</label>
          <input
            type="number"
            min={0}
            style={inputStyle()}
            value={form.story_points}
            onChange={e => setForm(f => ({ ...f, story_points: e.target.value }))}
            placeholder="0"
          />
        </div>
        <div style={{ flex: 2 }}>
          <label style={labelStyle}>{t('Toegewezen aan', 'Assigned to')}</label>
          <select
            style={inputStyle()}
            value={form.assigned_to}
            onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
          >
            <option value="">{t('Niemand', 'Nobody')}</option>
            {(members || []).map(m => (
              <option key={m.user_id} value={m.user_id}>
                {m.name || m.email}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('Prioriteit', 'Priority')}</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PRIORITIES.map(p => (
            <button
              key={p}
              onClick={() => setForm(f => ({ ...f, priority: p }))}
              style={{
                ...btnGhost({ padding: '6px 14px', fontSize: 12 }),
                background: form.priority === p ? `${PRIORITY_COLORS[p]}22` : 'transparent',
                borderColor: form.priority === p ? PRIORITY_COLORS[p] : 'rgba(255,255,255,0.15)',
                color: form.priority === p ? PRIORITY_COLORS[p] : C.silver,
              }}
            >
              {_isNl ? PRIORITY_LABELS[p].nl : PRIORITY_LABELS[p].en}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{t('Status', 'Status')}</label>
        <select
          style={inputStyle()}
          value={form.status}
          onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
        >
          {STORY_STATUSES.map(s => (
            <option key={s} value={s}>
              {s === 'backlog'     ? t('Backlog', 'Backlog')
               : s === 'todo'      ? t('Te doen', 'To Do')
               : s === 'in_progress'? t('Bezig', 'In Progress')
               : s === 'review'    ? t('Review', 'Review')
               :                     t('Klaar', 'Done')}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>{t('Sprint', 'Sprint')}</label>
        <select
          style={inputStyle()}
          value={form.sprint_id}
          onChange={e => setForm(f => ({ ...f, sprint_id: e.target.value }))}
        >
          <option value="">{t('Backlog (geen sprint)', 'Backlog (no sprint)')}</option>
          {(sprints || []).map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button style={btnGhost({ flex: 1 })} onClick={onClose}>
          {t('Annuleren', 'Cancel')}
        </button>
        <button style={btnPrimary({ flex: 1 })} onClick={handleSave} disabled={saving}>
          {saving ? t('Opslaan…', 'Saving…') : t('Opslaan', 'Save')}
        </button>
      </div>
    </Sheet>
  );
}

// ─── Move to Sprint picker ────────────────────────────────────────────────────
function MoveToSprintSheet({ story, sprints, onMove, onClose }) {
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);

  const handleMove = async () => {
    setSaving(true);
    try { await onMove(story, selected || null); } finally { setSaving(false); }
  };

  return (
    <Sheet onClose={onClose}>
      <div style={{ color: C.white, fontWeight: 700, fontSize: 17, marginBottom: 8 }}>
        {t('Verplaats naar sprint', 'Move to Sprint')}
      </div>
      <div style={{ color: C.silver, fontSize: 13, marginBottom: 18 }}>{story.title}</div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>{t('Kies sprint', 'Select sprint')}</label>
        <select style={inputStyle()} value={selected} onChange={e => setSelected(e.target.value)}>
          <option value="">{t('Backlog (geen sprint)', 'Backlog (no sprint)')}</option>
          {sprints.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button style={btnGhost({ flex: 1 })} onClick={onClose}>
          {t('Annuleren', 'Cancel')}
        </button>
        <button style={btnPrimary({ flex: 1 })} onClick={handleMove} disabled={saving}>
          {saving ? t('Verplaatsen…', 'Moving…') : t('Verplaatsen', 'Move')}
        </button>
      </div>
    </Sheet>
  );
}

// ─── Board Tab ────────────────────────────────────────────────────────────────
function BoardTab({ sprints, stories, members, canEdit, businessId, onStoriesChange, lang }) {
  _isNl = lang === 'nl';
  const activeSprints = sprints.filter(s => s.status === 'active');
  const [selectedSprintId, setSelectedSprintId] = useState(
    activeSprints.length ? activeSprints[0].id : ''
  );
  const [storySheet, setStorySheet] = useState(null); // { story?, defaultStatus }
  const [activeTab, setActiveTab] = useState(null); // for parent nav link

  const sprintStories = stories.filter(s => s.sprint_id === selectedSprintId);

  const storiesForCol = (status) =>
    sprintStories.filter(s => s.status === status);

  const handleAddToCol = (status) => {
    setStorySheet({ defaultStatus: status });
  };

  const handleEditStory = (story) => {
    setStorySheet({ story });
  };

  const handleSaveStory = async (data) => {
    if (storySheet.story) {
      await updateStory(businessId, storySheet.story.id, data);
    } else {
      await createStory(businessId, data);
    }
    await onStoriesChange();
    setStorySheet(null);
  };

  if (!sprints.length || !activeSprints.length) {
    return (
      <div style={{ padding: '32px 0', textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🏃</div>
        <div style={{ color: C.white, fontWeight: 600, marginBottom: 8 }}>
          {t('Geen actieve sprint', 'No active sprint')}
        </div>
        <div style={{ color: C.silver, fontSize: 13 }}>
          {t(
            'Ga naar het tabblad Sprints om een sprint te starten.',
            'Go to the Sprints tab to start a sprint.',
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Sprint selector */}
      <div style={{ marginBottom: 16 }}>
        <select
          style={inputStyle({ maxWidth: 260 })}
          value={selectedSprintId}
          onChange={e => setSelectedSprintId(e.target.value)}
        >
          {activeSprints.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Kanban board */}
      <div style={{
        display: 'flex',
        gap: 12,
        overflowX: 'auto',
        paddingBottom: 12,
      }}>
        {BOARD_COLUMNS.map(col => {
          const colColor = STATUS_COLUMN_COLORS[col];
          const label = _isNl ? STATUS_COLUMN_LABELS[col].nl : STATUS_COLUMN_LABELS[col].en;
          const colStories = storiesForCol(col);
          return (
            <div key={col} style={{
              minWidth: 200,
              flex: '0 0 220px',
              background: '#1a1a24',
              border: `1px solid rgba(255,255,255,0.07)`,
              borderTop: `2px solid ${colColor}`,
              borderRadius: 12,
              padding: '12px 10px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ color: colColor, fontWeight: 700, fontSize: 13 }}>
                  {label}
                  <span style={{ color: C.silver, fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                    {colStories.length}
                  </span>
                </div>
                {canEdit && (
                  <button
                    onClick={() => handleAddToCol(col)}
                    style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 2 }}
                    title={t('Story toevoegen', 'Add story')}
                  >
                    +
                  </button>
                )}
              </div>
              {colStories.map(story => (
                <StoryCard key={story.id} story={story} onPress={handleEditStory} compact allStories={stories} lang={lang} />
              ))}
              {colStories.length === 0 && (
                <div style={{ color: 'rgba(255,255,255,0.15)', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>
                  {t('Leeg', 'Empty')}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Story sheet */}
      {storySheet && (
        <StoryFormSheet
          initial={storySheet.story}
          defaultStatus={storySheet.defaultStatus}
          sprints={sprints}
          members={members}
          onSave={handleSaveStory}
          onClose={() => setStorySheet(null)}
          lang={lang}
        />
      )}
    </div>
  );
}

// ─── Backlog Tab ──────────────────────────────────────────────────────────────
function BacklogTab({ project, sprints, stories, members, canEdit, businessId, onStoriesChange, lang }) {
  _isNl = lang === 'nl';
  const [storySheet, setStorySheet] = useState(null);
  const [moveSheet, setMoveSheet] = useState(null);

  const backlogStories = stories.filter(s => !s.sprint_id);

  const grouped = PRIORITIES.reduce((acc, p) => {
    acc[p] = backlogStories.filter(s => s.priority === p);
    return acc;
  }, {});

  const handleSaveStory = async (data) => {
    if (storySheet?.story) {
      await updateStory(businessId, storySheet.story.id, data);
    } else {
      await createStory(businessId, { ...data, project_id: project.id });
    }
    await onStoriesChange();
    setStorySheet(null);
  };

  const handleMove = async (story, sprintId) => {
    await updateStory(businessId, story.id, { sprint_id: sprintId });
    await onStoriesChange();
    setMoveSheet(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: C.white, fontWeight: 600, fontSize: 15 }}>
          {t('Backlog', 'Backlog')}
          <span style={{ color: C.silver, fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            ({backlogStories.length})
          </span>
        </div>
        {canEdit && (
          <button style={btnPrimary({ padding: '8px 14px', fontSize: 13 })} onClick={() => setStorySheet({})}>
            + {t('Story toevoegen', 'Add Story')}
          </button>
        )}
      </div>

      {backlogStories.length === 0 && (
        <div style={{ color: C.silver, fontSize: 13, textAlign: 'center', padding: '32px 0' }}>
          {t('Backlog is leeg.', 'Backlog is empty.')}
        </div>
      )}

      {PRIORITIES.map(p => {
        const pStories = grouped[p];
        if (!pStories.length) return null;
        const pc = PRIORITY_COLORS[p];
        return (
          <div key={p} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: pc }} />
              <span style={{ color: pc, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {_isNl ? PRIORITY_LABELS[p].nl : PRIORITY_LABELS[p].en}
              </span>
              <span style={{ color: C.silver, fontSize: 12 }}>({pStories.length})</span>
            </div>
            {pStories.map(story => (
              <div key={story.id} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <StoryCard story={story} onPress={canEdit ? (s) => setStorySheet({ story: s }) : null} allStories={stories} lang={lang} />
                  </div>
                  {canEdit && (
                    <button
                      style={btnGhost({ fontSize: 11, padding: '5px 10px', whiteSpace: 'nowrap' })}
                      onClick={() => setMoveSheet(story)}
                    >
                      {t('+ Sprint', '+ Sprint')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {storySheet && (
        <StoryFormSheet
          initial={storySheet.story}
          defaultStatus="backlog"
          sprints={sprints}
          members={members}
          onSave={handleSaveStory}
          onClose={() => setStorySheet(null)}
          lang={lang}
        />
      )}

      {moveSheet && (
        <MoveToSprintSheet
          story={moveSheet}
          sprints={sprints}
          onMove={handleMove}
          onClose={() => setMoveSheet(null)}
        />
      )}
    </div>
  );
}

// ─── Sprints Tab ──────────────────────────────────────────────────────────────
function SprintsTab({ project, sprints, stories, canEdit, businessId, onSprintsChange, onStoriesChange, lang }) {
  _isNl = lang === 'nl';
  const [sprintSheet, setSprintSheet] = useState(null);
  const [expandedSprint, setExpandedSprint] = useState(null);
  const [saving, setSaving] = useState(null);

  const handleSaveSprint = async (data) => {
    if (sprintSheet?.sprint) {
      await updateSprint(businessId, sprintSheet.sprint.id, data);
    } else {
      await createSprint(businessId, { ...data, project_id: project.id, status: 'planning' });
    }
    await onSprintsChange();
    setSprintSheet(null);
  };

  const handleStatus = async (sprint, newStatus) => {
    setSaving(sprint.id);
    try {
      await updateSprint(businessId, sprint.id, { status: newStatus });
      await onSprintsChange();
    } finally { setSaving(null); }
  };

  const handleDelete = async (sprint) => {
    if (!window.confirm(t('Sprint verwijderen?', 'Delete sprint?'))) return;
    await deleteSprint(businessId, sprint.id);
    await onSprintsChange();
  };

  const sprintStoryCount = (sid) => stories.filter(s => s.sprint_id === sid).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: C.white, fontWeight: 600, fontSize: 15 }}>
          {t('Sprints', 'Sprints')}
        </div>
        {canEdit && (
          <button style={btnPrimary({ padding: '8px 14px', fontSize: 13 })} onClick={() => setSprintSheet({})}>
            + {t('Sprint', 'Sprint')}
          </button>
        )}
      </div>

      {sprints.length === 0 && (
        <div style={{ color: C.silver, fontSize: 13, textAlign: 'center', padding: '32px 0' }}>
          {t('Nog geen sprints. Voeg een sprint toe.', 'No sprints yet. Add a sprint.')}
        </div>
      )}

      {sprints.map(sprint => {
        const sc = SPRINT_STATUS_COLORS[sprint.status];
        const sl = _isNl ? SPRINT_STATUS_LABELS[sprint.status].nl : SPRINT_STATUS_LABELS[sprint.status].en;
        const isExpanded = expandedSprint === sprint.id;
        const spStories = stories.filter(s => s.sprint_id === sprint.id);

        return (
          <div key={sprint.id} style={{ ...panel({ marginBottom: 12 }) }}>
            {/* Sprint header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ color: C.white, fontWeight: 700, fontSize: 14 }}>{sprint.name}</span>
                  <Badge color={sc}>{sl}</Badge>
                </div>
                {sprint.goal && (
                  <div style={{ color: C.silver, fontSize: 12, marginBottom: 4, lineHeight: 1.4 }}>
                    {sprint.goal}
                  </div>
                )}
                <div style={{ color: C.silver, fontSize: 11 }}>
                  {fmt(sprint.start_date)} → {fmt(sprint.end_date)}
                  <span style={{ marginLeft: 10 }}>
                    {sprintStoryCount(sprint.id)} {t('stories', 'stories')}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setExpandedSprint(isExpanded ? null : sprint.id)}
                style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 16, padding: 4 }}
              >
                {isExpanded ? '▲' : '▼'}
              </button>
            </div>

            {/* Sprint actions */}
            {canEdit && (
              <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                {sprint.status === 'planning' && (
                  <button
                    style={btnGhost({ fontSize: 12, padding: '6px 12px', borderColor: '#4AC87A55', color: '#4AC87A' })}
                    onClick={() => handleStatus(sprint, 'active')}
                    disabled={saving === sprint.id}
                  >
                    ▶ {t('Starten', 'Start')}
                  </button>
                )}
                {sprint.status === 'active' && (
                  <button
                    style={btnGhost({ fontSize: 12, padding: '6px 12px', borderColor: '#66779955', color: '#667799' })}
                    onClick={() => handleStatus(sprint, 'completed')}
                    disabled={saving === sprint.id}
                  >
                    ✓ {t('Afronden', 'Complete')}
                  </button>
                )}
                <button
                  style={btnGhost({ fontSize: 12, padding: '6px 12px' })}
                  onClick={() => setSprintSheet({ sprint })}
                >
                  {t('Bewerken', 'Edit')}
                </button>
                <button
                  style={btnDanger({ fontSize: 12, padding: '6px 12px' })}
                  onClick={() => handleDelete(sprint)}
                >
                  {t('Verwijderen', 'Delete')}
                </button>
              </div>
            )}

            {/* Collapsible story list */}
            {isExpanded && (
              <div style={{ marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12 }}>
                {spStories.length === 0 ? (
                  <div style={{ color: C.silver, fontSize: 12, textAlign: 'center', padding: '8px 0' }}>
                    {t('Geen stories in deze sprint.', 'No stories in this sprint.')}
                  </div>
                ) : (
                  spStories.map(story => (
                    <StoryCard key={story.id} story={story} compact allStories={stories} lang={lang} />
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}

      {sprintSheet && (
        <SprintFormSheet
          initial={sprintSheet.sprint}
          onSave={handleSaveSprint}
          onClose={() => setSprintSheet(null)}
          lang={lang}
        />
      )}
    </div>
  );
}

// ─── Project Detail (Level 2) ─────────────────────────────────────────────────
function ProjectDetail({
  project, lang, businessId, role,
  onBack, onProjectUpdate,
}) {
  _isNl = lang === 'nl';
  const canEdit = ['owner', 'admin', 'manager'].includes(role);
  const [tab, setTab] = useState('board');
  const [sprints, setSprints]   = useState([]);
  const [stories, setStories]   = useState([]);
  const [members, setMembers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editSheet, setEditSheet] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sp, st, mb] = await Promise.all([
        getSprints(businessId, project.id),
        getStories(businessId, project.id),
        getMembers(businessId),
      ]);
      setSprints(Array.isArray(sp) ? sp : []);
      setStories(Array.isArray(st) ? st : []);
      setMembers(Array.isArray(mb) ? mb : []);
    } finally {
      setLoading(false);
    }
  }, [businessId, project.id]);

  const reloadSprints = useCallback(async () => {
    const sp = await getSprints(businessId, project.id);
    setSprints(Array.isArray(sp) ? sp : []);
  }, [businessId, project.id]);

  const reloadStories = useCallback(async () => {
    const st = await getStories(businessId, project.id);
    setStories(Array.isArray(st) ? st : []);
  }, [businessId, project.id]);

  useEffect(() => { load(); }, [load]);

  const handleSaveProject = async (data) => {
    await updateProject(businessId, project.id, data);
    onProjectUpdate({ ...project, ...data });
    setEditSheet(false);
  };

  const TAB_LIST = [
    { key: 'board',   nl: 'Board',   en: 'Board' },
    { key: 'backlog', nl: 'Backlog', en: 'Backlog' },
    { key: 'sprints', nl: 'Sprints', en: 'Sprints' },
  ];

  return (
    <div style={{ animation: 'fadeUp 0.3s ease both' }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: _T.accent, cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: C.white, fontWeight: 700, fontSize: 18 }}>{project.name}</span>
            <Badge color={PROJECT_STATUS_COLORS[project.status]} small>
              {project.status === 'active' ? t('Actief', 'Active') : t('Gearchiveerd', 'Archived')}
            </Badge>
          </div>
          {project.description && (
            <div style={{ color: C.silver, fontSize: 13, marginTop: 2 }}>{project.description}</div>
          )}
        </div>
        {canEdit && (
          <button style={btnGhost({ padding: '7px 14px', fontSize: 12 })} onClick={() => setEditSheet(true)}>
            {t('Bewerken', 'Edit')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 0 }}>
        {TAB_LIST.map(tb => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: tab === tb.key ? `2px solid ${_T.accent}` : '2px solid transparent',
              color: tab === tb.key ? _T.accent : C.silver,
              fontWeight: tab === tb.key ? 700 : 400,
              fontSize: 14,
              padding: '8px 14px',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {_isNl ? tb.nl : tb.en}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: C.silver }}>
          {t('Laden…', 'Loading…')}
        </div>
      ) : (
        <>
          {tab === 'board' && (
            <BoardTab
              sprints={sprints}
              stories={stories}
              members={members}
              canEdit={canEdit}
              businessId={businessId}
              onStoriesChange={reloadStories}
              lang={lang}
            />
          )}
          {tab === 'backlog' && (
            <BacklogTab
              project={project}
              sprints={sprints}
              stories={stories}
              members={members}
              canEdit={canEdit}
              businessId={businessId}
              onStoriesChange={reloadStories}
              lang={lang}
            />
          )}
          {tab === 'sprints' && (
            <SprintsTab
              project={project}
              sprints={sprints}
              stories={stories}
              canEdit={canEdit}
              businessId={businessId}
              onSprintsChange={reloadSprints}
              onStoriesChange={reloadStories}
              lang={lang}
            />
          )}
        </>
      )}

      {/* Edit project sheet */}
      {editSheet && (
        <ProjectFormSheet
          initial={project}
          onSave={handleSaveProject}
          onClose={() => setEditSheet(false)}
          lang={lang}
        />
      )}
    </div>
  );
}

// ─── Project List (Level 1) ───────────────────────────────────────────────────
function ProjectList({ projects, onSelect, onAdd, canEdit, lang }) {
  _isNl = lang === 'nl';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ color: C.white, fontWeight: 700, fontSize: 20 }}>
          {t('Projecten', 'Projects')}
        </div>
        {canEdit && (
          <button style={btnPrimary({ padding: '9px 18px', fontSize: 13 })} onClick={onAdd}>
            + {t('Project', 'Project')}
          </button>
        )}
      </div>

      {projects.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ color: C.white, fontWeight: 600, marginBottom: 6 }}>
            {t('Nog geen projecten', 'No projects yet')}
          </div>
          <div style={{ color: C.silver, fontSize: 13 }}>
            {t('Maak een project aan om te beginnen.', 'Create a project to get started.')}
          </div>
        </div>
      )}

      {projects.map(proj => {
        const pc = PROJECT_STATUS_COLORS[proj.status] || '#667799';
        return (
          <div
            key={proj.id}
            onClick={() => onSelect(proj)}
            style={{
              background: _T.bg3,
              borderBottom: `1px solid ${_T.cardBorderBot}`,
              borderTop: `1px solid rgba(255,255,255,0.04)`,
              borderRadius: 16,
              padding: '16px 18px',
              marginBottom: 10,
              cursor: 'pointer',
              transition: 'transform 0.15s',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>{proj.name}</span>
                  <Badge color={pc} small>
                    {proj.status === 'active' ? t('Actief', 'Active') : t('Gearchiveerd', 'Archived')}
                  </Badge>
                </div>
                {proj.description && (
                  <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.4 }}>
                    {proj.description}
                  </div>
                )}
                <div style={{ color: C.silver, fontSize: 11 }}>
                  {proj.story_count != null ? `${proj.story_count} ${t('stories', 'stories')}` : ''}
                  {proj.active_sprint_name
                    ? ` · ${t('Sprint', 'Sprint')}: ${proj.active_sprint_name}`
                    : ''}
                </div>
              </div>
              <span style={{ color: C.silver, fontSize: 18 }}>›</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Root Component ───────────────────────────────────────────────────────────
export default function BusinessProjectsScreen({ lang, businessId, currentUserId, role }) {
  _isNl = lang === 'nl';
  const canEdit = ['owner', 'admin', 'manager'].includes(role);

  const [projects, setProjects]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [addSheet, setAddSheet]           = useState(false);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProjects(businessId);
      setProjects(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const handleCreateProject = async (data) => {
    const result = await createProject(businessId, data);
    if (result?.error) throw new Error(result.error);
    setAddSheet(false);
    loadProjects();
  };

  const handleProjectUpdate = (updated) => {
    setProjects(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
    setSelectedProject(prev => ({ ...prev, ...updated }));
  };

  return (
    <div style={{ padding: '0 0 40px', minHeight: '100%' }}>
      <style>{CSS}</style>

      {selectedProject ? (
        <ProjectDetail
          key={selectedProject.id}
          project={selectedProject}
          lang={lang}
          businessId={businessId}
          role={role}
          onBack={() => setSelectedProject(null)}
          onProjectUpdate={handleProjectUpdate}
        />
      ) : (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: C.silver }}>
              {t('Laden…', 'Loading…')}
            </div>
          ) : (
            <ProjectList
              projects={projects}
              onSelect={setSelectedProject}
              onAdd={() => setAddSheet(true)}
              canEdit={canEdit}
              lang={lang}
            />
          )}

          {addSheet && (
            <ProjectFormSheet
              onSave={handleCreateProject}
              onClose={() => setAddSheet(false)}
              lang={lang}
            />
          )}
        </>
      )}
    </div>
  );
}
